import { SupportedRegions } from "@nova/constants"
import { getApiGatewayDomainForRegion, config, currentRegion } from "@nova/configuration"
import type { ApiRegistry } from "@nova/contracts"

/**
 * Extracts the structured error from a thrown fetchApiGateway error response.
 * When the API returns a non-OK JSON response, the body is thrown directly.
 * Those error bodies typically have the shape `{ error: { message: string; details: unknown } }`.
 * Returns `{ message, details }`, or `undefined` if the shape doesn't match.
 */
export const extractApiGatewayError = (
    error: unknown
): { message: string; details: unknown } | undefined => {
    if (error && typeof error === "object" && "error" in error) {
        const { error: errorObject } = error as { error: unknown }
        if (errorObject && typeof errorObject === "object" && "message" in errorObject) {
            const { message, details } = errorObject as { message: unknown; details?: unknown }
            if (typeof message === "string") {
                return { message, details }
            }
        }
    }
    return undefined
}

export const getApiGatewayDomain = () => {
    const region = currentRegion()
    return getApiGatewayDomainForRegion(region)
}

type AppRoute = keyof ApiRegistry
type GetParams<T extends AppRoute> = ApiRegistry[T]["params"]
type GetBody<T extends AppRoute> = ApiRegistry[T]["inputBody"]

type FetchApiGateway2OptionsBase<T extends AppRoute> = (GetParams<T> extends never
    ? { params?: never }
    : { params: GetParams<T> }) &
    (GetBody<T> extends never
        ? { body?: never }
        : unknown extends GetBody<T>
          ? { body?: never }
          : { body: GetBody<T> }) &
    Omit<RequestInit, "body"> & {
        region?: SupportedRegions
        query?: string
        includeCredentials?: boolean
    }

type FetchApiGateway2OptionsRaw<T extends AppRoute> = FetchApiGateway2OptionsBase<T> & {
    rawResponse: true
}

type FetchApiGateway2OptionsParsed<T extends AppRoute> = FetchApiGateway2OptionsBase<T> & {
    rawResponse?: false
}

export async function fetchApiGateway<T extends AppRoute>(
    route: T,
    options: FetchApiGateway2OptionsRaw<T>
): Promise<Response>
export async function fetchApiGateway<T extends AppRoute>(
    route: T,
    options?: FetchApiGateway2OptionsParsed<T>
): Promise<ApiRegistry[T]["outputType"]>
export async function fetchApiGateway<T extends AppRoute>(
    route: T,
    options?: FetchApiGateway2OptionsBase<T> & { rawResponse?: boolean }
) {
    const [method, pathTemplate] = route.split(" ")
    let url = pathTemplate

    const {
        region,
        params,
        body,
        query,
        credentials,
        includeCredentials = true,
        headers,
        rawResponse = false,
        ...rest
    } = options || {}

    const baseApiGatewayURL = region ? getApiGatewayDomainForRegion(region) : getApiGatewayDomain()

    if (params) {
        Object.entries(params).forEach(([key, value]) => {
            url = url.replace(`:${key}`, encodeURIComponent(String(value)))
        })
    }

    if (query) url += "?" + query

    const isSameOrigin = baseApiGatewayURL.startsWith(config.get("domain"))
    const credentialMode =
        credentials ?? (includeCredentials ? "include" : isSameOrigin ? "same-origin" : "omit")

    const init: RequestInit = {
        ...rest,
        credentials: credentialMode,
        method: method.toUpperCase(),
    }

    // Always include JSON content-type so preflights reflect required headers
    init.headers = {
        "Content-Type": "application/json",
        ...(headers ?? {}),
    }
    if (body !== undefined) {
        init.body = JSON.stringify(body)
    }

    const response = await fetch(baseApiGatewayURL + url, init)

    if (rawResponse) return response

    if (!response.ok) {
        const contentType = response.headers.get("content-type")?.toLowerCase()
        if (contentType?.startsWith("application/json")) {
            const errorData = await response.json()
            throw errorData
        }

        const error = await response.text()
        throw new Error(`API Error: ${response.status} ${error}`)
    }

    if (response.status === 204) return ""

    // headers.get is case insensitive on key, case sensitive on value
    // content-type header is required by backend at runtime
    const contentType = response.headers.get("content-type")?.toLowerCase()
    if (contentType?.startsWith("application/json")) {
        return response.json()
    } else if (contentType?.startsWith("text")) {
        return response.text()
    } else return await response.arrayBuffer()
}
